import type {
  CrossroadsLog,
  GroupMetadata,
} from '@/api/hooks/useIntegrationLogs'
import { formatTimestamp } from '@/utils/dateFormatters'
import { Card, Descriptions } from 'antd'
import _ from 'lodash'
import type { ChangeSnapshot } from './OrderChangeSummary'

const CARD_STYLE = { marginBottom: 16 }
const DESCRIPTIONS_CONTENT_STYLE = {
  display: 'flex',
  justifyContent: 'flex-end',
}

/** Type guard for order change metadata */
export function isOrderChangeMetadata(
  metadata: CrossroadsLog['metadata'] | undefined,
): metadata is CrossroadsLog['metadata'] & {
  type: 'order_change'
  change_types: string[]
  before_change?: ChangeSnapshot | null
  after_change?: ChangeSnapshot | null
} {
  return metadata?.type === 'order_change'
}

type LogDetailInfoBoxProps = React.PropsWithChildren<{
  title: string
  /** Rule each row off from the next (see .log-info-divided in overrides.less) */
  divided?: boolean
}>

function LogDetailInfoBox({ title, divided, children }: LogDetailInfoBoxProps) {
  return (
    <Card title={title} size='small' style={CARD_STYLE}>
      <Descriptions
        column={1}
        size='small'
        className={divided ? 'log-info-divided' : undefined}
        contentStyle={DESCRIPTIONS_CONTENT_STYLE}
      >
        {children}
      </Descriptions>
    </Card>
  )
}

function basicRows(log: CrossroadsLog) {
  return [
    log.date && (
      <Descriptions.Item key='date' label='Date' span={1}>
        {formatTimestamp(log.date)}
      </Descriptions.Item>
    ),
    isOrderChangeMetadata(log.metadata) && (
      <Descriptions.Item key='change-types' label='Change Types'>
        {log.metadata.change_types.map((ct) => _.startCase(ct)).join(', ')}
      </Descriptions.Item>
    ),
  ]
}

function recordTypeRow(log: CrossroadsLog) {
  return (
    <Descriptions.Item key='record-type' label='Record Type'>
      {_.startCase(log.metadata?.type)}
    </Descriptions.Item>
  )
}

function batchIdRow(groupMetadata?: GroupMetadata | null) {
  return (
    groupMetadata?.type === 'supply_plan_batch' && (
      <Descriptions.Item key='batch-id' label='Batch ID'>
        {groupMetadata.batch_id}
      </Descriptions.Item>
    )
  )
}

function flowRow(log: CrossroadsLog) {
  return (
    <Descriptions.Item key='flow' label='Flow'>
      {`${log.routing?.origin_tenant_name} (${log.routing?.origin_instance_name}) → ${log.routing?.destination_tenant_name} (${log.routing?.destination_instance_name})`}
    </Descriptions.Item>
  )
}

/** Only the V3 error details (request/setup/supply/system) carry this — it
 * names the system that turned the request down. */
function rejectedByRow(log: CrossroadsLog) {
  const rejectedBy =
    'rejected_by' in log.detail ? log.detail.rejected_by : undefined
  return (
    rejectedBy && (
      <Descriptions.Item key='rejected-by' label='Rejected By'>
        {rejectedBy}
      </Descriptions.Item>
    )
  )
}

function legacyOrderRows(groupMetadata?: GroupMetadata | null) {
  return [
    groupMetadata?.type === 'order' &&
      groupMetadata.order?.origin_order_number && (
        <Descriptions.Item key='source-order' label='Source Order Number'>
          {groupMetadata.order.origin_order_number}
        </Descriptions.Item>
      ),
    groupMetadata?.type === 'order' &&
      groupMetadata.order?.destination_order_number && (
        <Descriptions.Item key='target-order' label='Target Order Number'>
          {groupMetadata.order.destination_order_number}
        </Descriptions.Item>
      ),
    groupMetadata?.type === 'order' && groupMetadata.order?.order_id && (
      <Descriptions.Item key='order-id' label='Order ID'>
        {groupMetadata.order.order_id}
      </Descriptions.Item>
    ),
  ]
}

/** Order numbers labelled by the tenant they belong to. The partner's row
 * always renders -- a missing partner order number is itself informative. */
function tenantOrderRows(
  log: CrossroadsLog,
  groupMetadata?: GroupMetadata | null,
) {
  if (groupMetadata?.type !== 'order') return []
  const originName = log.routing?.origin_tenant_name ?? 'Source'
  const destinationName = log.routing?.destination_tenant_name ?? 'Partner'
  return [
    groupMetadata.order?.origin_order_number && (
      <Descriptions.Item
        key='source-order'
        label={`${originName} Order Number`}
      >
        {groupMetadata.order.origin_order_number}
      </Descriptions.Item>
    ),
    <Descriptions.Item
      key='target-order'
      label={`${destinationName} Order Number`}
    >
      {groupMetadata.order?.destination_order_number ?? '—'}
    </Descriptions.Item>,
  ]
}

function recordRows(log: CrossroadsLog, groupMetadata?: GroupMetadata | null) {
  return [
    recordTypeRow(log),
    ...legacyOrderRows(groupMetadata),
    batchIdRow(groupMetadata),
    flowRow(log),
  ]
}

function informationRows(
  log: CrossroadsLog,
  groupMetadata?: GroupMetadata | null,
) {
  return [
    ...basicRows(log),
    recordTypeRow(log),
    ...tenantOrderRows(log, groupMetadata),
    batchIdRow(groupMetadata),
    flowRow(log),
    rejectedByRow(log),
  ]
}

export function BasicInformationCard({ log }: { log: CrossroadsLog }) {
  return (
    <LogDetailInfoBox title='Basic Information'>
      {basicRows(log)}
    </LogDetailInfoBox>
  )
}

type RecordInformationCardProps = {
  log: CrossroadsLog
  groupMetadata?: GroupMetadata | null
}

export function RecordInformationCard({
  log,
  groupMetadata,
}: RecordInformationCardProps) {
  return (
    <LogDetailInfoBox title='Record Information'>
      {recordRows(log, groupMetadata)}
    </LogDetailInfoBox>
  )
}

/** Basic and Record Information merged into a single box. */
export function InformationCard({
  log,
  groupMetadata,
}: RecordInformationCardProps) {
  return (
    <LogDetailInfoBox title='Information' divided>
      {informationRows(log, groupMetadata)}
    </LogDetailInfoBox>
  )
}
